import React, { type ComponentType, useEffect } from 'react';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { TableSettingsPanel as RequestTypesTableViewSettingsPanel } from '@atlassian/jira-request-types-table/src/ui/view-settings-panel/panel/index.tsx';
import { ServiceDeskAnalyticsContext } from '@atlassian/jira-servicedesk-analytics/src/ui/servicedesk-analytics-context/index.tsx';
import { JsdGettingStartedPanel } from '@atlassian/jira-servicedesk-getting-started-panel/src/async.tsx';
import { BUSINESS } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/constants.tsx';
import { useJsmOnboardingType } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/main.tsx';
import { JswOnboardingChecklist } from '@atlassian/jira-software-onboarding-quickstart/src/ui/async.tsx';
import {
	shouldRouteShowQuickstartOnboarding,
	shouldRenderRequestTypesTableViewSettings,
} from '../../common/utils.tsx';

/**
 * Chooses and renders the appropriate sidebar component based on the project's context and the user's current state. This decision is influenced by factors such as the user's eligibility for experiments and their stage in the onboarding process, aiming to enhance the user experience by presenting the most relevant information and actions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RightSidebarsInternal: ComponentType<Record<any, any>> = () => {
	const projectContext = useProjectContext();
	const route = useCurrentRoute();
	const projectContextData = projectContext?.data;
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [fetchOnboardingType, projectContextData]);

	if (
		jsmOnboardingData?.onboardingType === BUSINESS &&
		shouldRouteShowQuickstartOnboarding(route.name)
	) {
		if (shouldRenderRequestTypesTableViewSettings(route.name)) {
			return (
				<>
					{projectContextData?.projectKey && (
						<RequestTypesTableViewSettingsPanel projectKey={projectContextData.projectKey} />
					)}
					<JswOnboardingChecklist key="jsm-quickstart-checklist" />
				</>
			);
		}
		return <JswOnboardingChecklist key="jsm-quickstart-checklist" />;
	}
	if (shouldRenderRequestTypesTableViewSettings(route.name)) {
		return (
			<>
				{projectContextData?.projectKey && (
					<RequestTypesTableViewSettingsPanel projectKey={projectContextData.projectKey} />
				)}
				<JsdGettingStartedPanel key="jsm-getting-started-panel" />
			</>
		);
	}
	return <JsdGettingStartedPanel key="jsm-getting-started-panel" />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RightSidebars: ComponentType<Record<any, any>> = () => (
	<ServiceDeskAnalyticsContext>
		<RightSidebarsInternal />
	</ServiceDeskAnalyticsContext>
);
export default RightSidebars;
